/**
 * #1124 — per-venue frame counters for the Data pressure card.
 *
 * "Received" counts every frame this tab parsed (HydraClient.handleMessage,
 * replay excluded), including frames the store then drops because another feed
 * owns the row or no view needs the book. "Shown" counts books that got past
 * the store's no-view bail (inside pushFeedLog), so shown ÷ received is the
 * share of the book flood that reached something on screen.
 *
 * Hot path: every count is one indexed `++` on a typed array allocated once.
 * No allocation, no clock, no store or React touch. The counters never reset;
 * readers take deltas with `counterDelta`, which is correct across the 2^32 wrap.
 */

/** Indexes into `receivedCounts`. */
export const SLOT = {
  kalshiBook: 0,
  kalshiMarket: 1,
  polyIntlBook: 2,
  polyIntlMarket: 3,
  polyUsBook: 4,
  polyUsMarket: 5,
  oddsUnabated: 6,
  oddsOddspapi: 7,
} as const;

/** Indexes into `shownCounts`. Poly US isn't measured (its books feed the US
 *  board's adapter whether or not a view shows them), and odds carry no books. */
export const SHOWN = {
  kalshi: 0,
  polyIntl: 1,
} as const;

export const receivedCounts = new Uint32Array(8);
export const shownCounts = new Uint32Array(2);

/** The store's own venue rule for a ticker (marketStore ingestOrderbook). */
function isPolyUs(ticker: string): boolean {
  return ticker.startsWith('PMUS-');
}

export function countReceivedBook(ticker: string): void {
  if (isPolyUs(ticker)) receivedCounts[SLOT.polyUsBook]++;
  else if (ticker.startsWith('PM-')) receivedCounts[SLOT.polyIntlBook]++;
  else receivedCounts[SLOT.kalshiBook]++;
}

export function countReceivedMarket(ticker: string, source: 'kalshi' | 'polymarket'): void {
  if (isPolyUs(ticker)) receivedCounts[SLOT.polyUsMarket]++;
  else if (source === 'polymarket') receivedCounts[SLOT.polyIntlMarket]++;
  else receivedCounts[SLOT.kalshiMarket]++;
}

export function countReceivedConsensus(): void {
  receivedCounts[SLOT.oddsUnabated]++;
}

export function countReceivedOpOdds(): void {
  receivedCounts[SLOT.oddsOddspapi]++;
}

/** Called from pushFeedLog for an orderbook entry. */
export function countShownBook(source: string): void {
  if (source === 'kalshi') shownCounts[SHOWN.kalshi]++;
  else if (source === 'polymarket') shownCounts[SHOWN.polyIntl]++;
}

/** `now - before` for a Uint32 counter, correct across one wrap. */
export function counterDelta(now: number, before: number): number {
  return (now - before) >>> 0;
}

export function __resetFeedCountersForTests(): void {
  receivedCounts.fill(0);
  shownCounts.fill(0);
}
